import { validate as uuidValidate } from "uuid";

import {
    isNonEmptyArray,
    isNonEmptyString,
    isNonEmptyObject,
    isObject,
} from "@cayuse/shared-react.utils.type-checks";
import { encodeSearchTerms } from "@cayuse/shared-react.utils.helpers";
import { notify } from "@cayuse/shared-react.components.banner-notification";
import { escapeForElasticSearchQuery, spUrl } from "utils/helpers";
import { notifyError } from "utils/errorNotification";
import { patch } from "utils/proxy";
import { IS_DEV, COLUMN_HEADER_KEYS, API_USERS } from "globals/constants";
import { api } from "services";
import { executeRequest } from "@cayuse/shared-react.utils.http";

const PATCH_PROPOSAL_BATCH_URL = spUrl("/api/proposals/adminMembers");
const PATCH_AWARD_BATCH_URL = spUrl("/api/awards/adminMembers");

const typeUrlHash = {
    [COLUMN_HEADER_KEYS.PROPOSAL]: PATCH_PROPOSAL_BATCH_URL,
    [COLUMN_HEADER_KEYS.AWARD]: PATCH_AWARD_BATCH_URL,
};

const typeIdKeyHash = {
    [COLUMN_HEADER_KEYS.PROPOSAL]: "proposalId",
    [COLUMN_HEADER_KEYS.AWARD]: "awardId",
};

export const findUsers = async ({
    pageOffset,
    pageLimit,
    searchTerms,
    includeInactive = false,
}) => {
    if (pageOffset === undefined) {
        throw Error("page offset cannot be null");
    } else if (pageLimit === undefined) {
        throw Error("page limit cannot be null");
    } else if (!isNonEmptyArray(searchTerms)) {
        throw Error("searchTerms cannot be null or empty");
    }

    try {
        const data = await api.users.find({
            search: searchTerms,
            page: pageOffset,
            size: pageLimit,
            includeInactive,
        });

        return data?.content ?? [];
    } catch (error) {
        const message = IS_DEV ? error.message : "An error has occurred";
        notifyError(message, error);
    }
};

const getBatchURL = (formType) => {
    return formType in typeUrlHash ? typeUrlHash[formType] : undefined;
};

const getTypeIdKey = (formType) => {
    return formType in typeIdKeyHash ? typeIdKeyHash[formType] : undefined;
};

// not using anymore
export const updateAdminTeam = (adminTeamMembers, formType, formId) => {
    /* currently depends on having a full team object */
    const url = getBatchURL(formType);

    const idKey = getTypeIdKey(formType);

    const bulkPartialUpdateProposals = [
        /* array to make the bulk endpoint happy */
        {
            [idKey]: formId,
            adminTeamMembers,
        },
    ];

    if (isNonEmptyString(url)) {
        return patch(url, bulkPartialUpdateProposals).then((results) => {
            return results;
        });
        // .then(results => handleBulkPatchResponse(results, bulkPartialUpdateProposals));
    }
};

/**
 * This method is called from either the Dashboard page or the Proposal Detail page, so it's designed
 * to expect only ProposalFeedItem objects. Proposal objects are unavailable on the Dashboard.
 * It's up to the caller to update its view based on the results of this operation itself.
 * @param {*} formTypeFeed
 * @param {*} user
 * @param {*} role
 * @param {*} formType
 * @returns An array of objects mapping proposal Jest ids to arrays of admin team members.
 */
export const addAdminTeamMemberWithRole = async (
    formTypeFeed,
    user,
    role,
    formType
) => {
    const formTypeFeedItems = Array.isArray(formTypeFeed)
        ? formTypeFeed
        : [formTypeFeed];
    const hasFormTypeFeedItems = isNonEmptyArray(formTypeFeedItems);
    const hasUser = isNonEmptyObject(user);
    const hasRole = isNonEmptyObject(role);

    if (!hasFormTypeFeedItems || !hasUser || !hasRole) {
        console.warn("InstanceFeedItems, user, and role are all required.");

        return;
    }

    const adminTeamMemberCandidate = {
        personId: user.personId,
        roleId: role.id,
        fullName: user.fullName,
        roleName: role.name,
    };

    const bulkPartialUpdate = formTypeFeedItems.map((formTypeFeedItem) => {
        const typeIdKey = [getTypeIdKey(formType)];

        const formTypeElement = {
            //This can be proposal or award
            id: formTypeFeedItem.id,
            [typeIdKey]: formTypeFeedItem[typeIdKey],
            adminTeamMembers: [],
        };

        if (!Array.isArray(formTypeFeedItem.adminTeamMemberData)) {
            formTypeElement.adminTeamMembers.push(adminTeamMemberCandidate);

            return formTypeElement;
        }

        if (formTypeFeedItem.adminTeamMemberData.length === 0) {
            formTypeElement.adminTeamMembers.push(adminTeamMemberCandidate);

            return formTypeElement;
        }

        const isUserToAddNotPresent =
            formTypeFeedItem.adminTeamMemberData.every(
                (adminTeamMember) =>
                    adminTeamMemberCandidate.personId !==
                    adminTeamMember.personId
            );

        if (isUserToAddNotPresent) {
            formTypeFeedItem.adminTeamMemberData.push(adminTeamMemberCandidate);
            formTypeElement.adminTeamMembers =
                formTypeFeedItem.adminTeamMemberData;

            return formTypeElement;
        }

        formTypeElement.adminTeamMembers = formTypeFeedItem.adminTeamMemberData;

        return formTypeElement;
    });

    let promise;
    if (formType === COLUMN_HEADER_KEYS.PROPOSAL) {
        promise = api.proposal.updateAdminMembers;
    } else if (formType === COLUMN_HEADER_KEYS.AWARD) {
        promise = api.awards.updateAdminMembers;
    } else {
        throw new Error("Unknown form type");
    }

    try {
        const results = await promise(bulkPartialUpdate);

        return handleBulkPatchResponse(results, bulkPartialUpdate);
    } catch (error) {
        const message = IS_DEV ? error.message : "An error has occurred";
        notifyError(message, error);
    } finally {
        const message = `${user.fullName} assigned as ${role.name} .`;
        notify(message, { type: "success" });
    }
};

export const removeAdminTeamMember = (
    adminTeamMember,
    proposalFeedItem,
    adminTeamMembers
) => {
    const isAdminTeamMemberPresent = isObject(adminTeamMember);
    const isProposalFeedItemPresent = isObject(proposalFeedItem);
    const isProposalIdPresent = isNonEmptyString(proposalFeedItem.proposalId);
    const isAdminTeamMemberListPresent = isNonEmptyArray(adminTeamMembers);

    if (
        !isAdminTeamMemberPresent ||
        !isProposalFeedItemPresent ||
        !isProposalIdPresent ||
        !isAdminTeamMemberListPresent
    ) {
        console.error("proposalId or adminTeamMembers is empty.");

        return;
    }

    const adminTeamMembersMinusOne = adminTeamMembers.filter(
        (teamMember) => adminTeamMember.personId !== teamMember.personId
    );

    const proposalPatchData = [
        {
            id: proposalFeedItem.id,
            proposalId: proposalFeedItem.proposalId,
            adminTeamMembers: adminTeamMembersMinusOne,
        },
    ];

    return patch(PATCH_PROPOSAL_BATCH_URL, proposalPatchData).then((results) =>
        handleBulkPatchResponse(results, proposalPatchData)
    );
};

export const searchUsers = (searchText, searchOptions) => {
	const searchTerms = [
		{
			key: "fullName",
			val: `${escapeForElasticSearchQuery(searchText.toLocaleLowerCase()).trim()}`,
		}, // Search by substring inside fullName
		...searchOptions.searchParameters,
	];

	const search = encodeSearchTerms(searchTerms);

	let url = `${API_USERS}?search=${search}`;

	const accountId = searchOptions.searchParameters.find(param => param.key === "userAccountId");
	if (accountId && uuidValidate(accountId.val)) {
		url = `${url}&activeOnly=false`;
	}

	return executeRequest(url);
};

const handleBulkPatchResponse = (results, proposalPatchData) => {
    const isUpdateSuccessful = results.every(
        (bulkReportItem) => bulkReportItem.success
    );
    if (!isUpdateSuccessful) {
        return Promise.reject(results);
    }

    return Promise.resolve(proposalPatchData);
};
